
import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  HiOutlineArrowUpRight,
  HiOutlineScale,
  HiOutlineShieldCheck,
  HiOutlineBookOpen,
  HiOutlineCheckBadge,
} from "react-icons/hi2";

import Section from "./layout/Section";
import { aboutUsData } from "../constants";
import { ActionButton } from "./layout";

/* ============================================
   HIGHLIGHT ICONS
============================================ */

const highlightIcons = [
  HiOutlineShieldCheck,
  HiOutlineBookOpen,
  HiOutlineCheckBadge,
];

/* ============================================
   ABOUT US - HOME SECTION
============================================ */

function AboutUs() {
  const { i18n } = useTranslation();

  // i18n controls the content language only.
  // Page direction is managed globally.
  const language = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "ar"
  )
    .toLowerCase()
    .startsWith("en")
    ? "en"
    : "ar";

  const content = aboutUsData[language];

  const { introduction, hero } = content;

  return (
    <Section
      className="
        relative isolate overflow-hidden
        bg-skin-page
        text-skin-text
        transition-colors duration-300
      "
    >
      {/* Decorative background */}
      <div
        aria-hidden="true"
        className="
          pointer-events-none absolute
          -end-40 -top-40
          h-[450px] w-[450px]
          rounded-full
          bg-skin-accent/[0.035]
          blur-3xl
        "
      />

      <div className="relative w-full py-20 lg:py-28 xl:py-32">

        {/* ======================================
            MAIN GRID

            Text first, image second.
            RTL / LTR handled globally.
        ====================================== */}

        <div
          className="
            grid grid-cols-1
            items-center
            gap-14
            lg:grid-cols-2
            lg:gap-16
            xl:gap-24
          "
        >

          {/* ======================================
              CONTENT
          ====================================== */}

          <div className="min-w-0">

            {/* Eyebrow */}
            <div className="mb-6 flex items-center gap-3">

              <span
                aria-hidden="true"
                className="
                  h-px w-9 shrink-0
                  bg-skin-accent
                "
              />

              <span className="eyebrow text-skin-accent">
                {introduction.eyebrow}
              </span>

            </div>

            {/* Main Title */}
            <h2
              className="
                section-title
                max-w-[650px]
                text-skin-heading
              "
            >
              {introduction.title}
            </h2>

            {/* Description */}
            <div className="mt-7 flex flex-col gap-5">

              {introduction.paragraphs
                .slice(0, 2)
                .map((paragraph, index) => (
                  <p
                    key={index}
                    className="
                      body-1
                      text-skin-text
                    "
                  >
                    {paragraph}
                  </p>
                ))}

            </div>

            {/* ======================================
                PROFESSIONAL HIGHLIGHTS
            ====================================== */}

            <div
              className="
                mt-9 grid grid-cols-1
                gap-3
                sm:grid-cols-3
              "
            >

              {hero.traits.map((trait, index) => {
                const Icon =
                  highlightIcons[
                    index % highlightIcons.length
                  ];

                return (
                  <div
                    key={index}
                    className="
                      group flex min-w-0
                      items-center gap-3
                      rounded-xl
                      border border-skin-border
                      bg-skin-surface
                      px-4 py-4
                      transition-all duration-300

                      hover:border-skin-accent/40
                      hover:bg-skin-soft
                    "
                  >
                    {/* Icon */}
                    <div
                      className="
                        flex h-10 w-10
                        shrink-0 items-center
                        justify-center
                        rounded-lg
                        bg-skin-soft
                        text-skin-accent
                        transition-colors duration-300

                        group-hover:bg-skin-accent/10
                      "
                    >
                      <Icon
                        size={22}
                        aria-hidden="true"
                      />
                    </div>

                    {/* Text */}
                    <h3
                      className="
                        body-3 min-w-0
                        font-semibold
                        text-skin-heading
                      "
                    >
                      {trait}
                    </h3>

                  </div>
                );
              })}

            </div>

            {/* ======================================
                PROFESSIONAL STATEMENT
            ====================================== */}

            <div
              className="
                mt-8 flex items-start gap-4
                rounded-xl
                border border-skin-border
                bg-skin-soft
                p-5
                sm:p-6
              "
            >
              <div
                className="
                  flex h-10 w-10
                  shrink-0 items-center
                  justify-center
                  rounded-lg
                  bg-skin-accent/10
                  text-skin-accent
                "
              >
                <HiOutlineScale
                  size={23}
                  aria-hidden="true"
                />
              </div>

              <p
                className="
                  body-2
                  self-center
                  text-skin-text
                "
              >
                {introduction.footnote}
              </p>
            </div>

            {/* ======================================
                CTA BUTTON
            ====================================== */}

            <div className="mt-9 flex">
            <Link to="/AboutUs" className="group inline-flex">
  <ActionButton
    text="تعرف على المكتب"
    icon={<HiOutlineArrowUpRight />}
    variant="glass"
  />
</Link>

            </div>

          </div>

          {/* ======================================
              IMAGE AREA
          ====================================== */}

          <div className="relative min-w-0">

            {/* Main image frame */}
            <div
              className="
                relative
                rounded-[26px]
                border border-skin-border
                bg-skin-surface
                p-2
                shadow-elevated

                dark:shadow-[0_20px_65px_rgba(0,0,0,0.16)]
              "
            >

              {/* Decorative corner */}
              <div
                aria-hidden="true"
                className="
                  pointer-events-none
                  absolute -end-4 -top-4
                  h-28 w-28
                  border-e border-t
                  border-skin-accent/60
                "
              />

              {/* Image */}
              <div
                className="
                  group relative
                  aspect-[5/4]
                  overflow-hidden
                  rounded-[20px]
                  bg-skin-soft
                  sm:aspect-[6/5]
                  lg:aspect-[5/4]
                "
              >

                <img
                  src={aboutUsData.images.section}
                  alt={introduction.imageAlt}
                  loading="lazy"
                  decoding="async"
                  className="
                    h-full w-full
                    object-cover
                    object-center
                    transition-transform
                    duration-700 ease-out

                    group-hover:scale-[1.04]
                  "
                />

                {/* Soft image gradient */}
                <div
                  aria-hidden="true"
                  className="
                    pointer-events-none
                    absolute inset-0
                    bg-gradient-to-t
                    from-[#071421]/75
                    via-transparent
                    to-transparent
                  "
                />

                {/* Subtle top badge */}
                <div
                  className="
                    absolute start-5 top-5
                    flex h-11 w-11
                    items-center justify-center
                    rounded-xl
                    border border-white/20
                    bg-[#102031]/70
                    text-[#D7B77F]
                    backdrop-blur-lg
                  "
                >
                  <HiOutlineScale
                    size={24}
                    aria-hidden="true"
                  />
                </div>

                {/* ==================================
                    IMAGE CAPTION
                ================================== */}

                <div
                  className="
                    absolute bottom-0 start-0
                    w-full p-5
                    sm:p-7
                  "
                >
                  <div
                    className="
                      max-w-[440px]
                      rounded-xl
                      border border-white/15
                      bg-[#0B1726]/65
                      p-4
                      text-white
                      shadow-lg
                      backdrop-blur-xl
                      sm:p-5
                    "
                  >

                    <div
                      className="
                        mb-3 flex items-center
                        gap-3 text-[#D7B77F]
                      "
                    >
                      <HiOutlineScale
                        size={20}
                        aria-hidden="true"
                      />

                      <span
                        aria-hidden="true"
                        className="
                          h-px w-8
                          bg-[#D7B77F]/70
                        "
                      />
                    </div>

                    <p className="body-3 text-white/90">
                      {introduction.imageCaption}
                    </p>

                  </div>
                </div>

              </div>
            </div>

            {/* Bottom decorative detail */}
            <div
              aria-hidden="true"
              className="
                mt-6 flex items-center gap-3
              "
            >
              <span
                className="
                  h-px w-12
                  bg-skin-accent/70
                "
              />

              <span
                className="
                  h-1.5 w-1.5
                  rounded-full
                  bg-skin-accent
                "
              />

              <span
                className="
                  h-px w-20
                  bg-skin-border
                "
              />
            </div>

          </div>

        </div>
      </div>
    </Section>
  );
}

export default AboutUs;
